import React, { useEffect, useMemo, useRef, useState } from 'react';
import { KeyRound, Pencil, Search, Shield, UserCog, UserPlus, XCircle } from 'lucide-react';
import PasswordResetLinkDialog from './PasswordResetLinkDialog';
import UserInviteCredentialsDialog from './UserInviteCredentialsDialog';
import TableRowKebabMenu from './TableRowKebabMenu';
import type { LawFirmUserRow } from '../../services/ClientLicensesApiClient';
import type { ManagedDirectoryUsersAdapter } from '../../services/managedDirectoryUsersApi';
import { shellMainPaddingX } from '../../utils/shellLayoutClasses';
import {
  clientDataPanelClass,
  clientEmptyStateCardClass,
  clientSurfaceCardClass,
} from '../../utils/clientChromeClasses';

export type ManagedDirectoryPreset = 'platform' | 'firmWorkspace' | 'companyClient';

type CopySpec = {
  loadFailed: string;
  lede: string;
  summaryTotal: string;
  summaryActive: string;
  loading: string;
  emptyTitle: string;
  emptyBody: string;
  emptyInviteCta: string;
  emptyNoPermission: string;
  searchPlaceholder: string;
  searchAriaLabel: string;
  inviteBtnTitle: string;
  inviteBtn: string;
  addTitle: string;
  addHeroTitle: string;
  addHeroBody: React.ReactNode;
  addSubmit: string;
  addSubmitting: string;
  mutateErrorInvite: string;
  editTitle: string;
  editHeroTitle: string;
  editHeroBody: React.ReactNode;
  editSubmit: string;
  editSubmitting: string;
  mutateErrorEdit: string;
  inviteDlgTitle: string;
  inviteDlgLede: string;
  kebabEditDesc: string;
  noSearchMatches: string;
  inviteTempPasswordNote: string;
  addIconTitle: string;
  editIconTitle: string;
  emptyIcon: 'shield' | 'userCog';
};

function copyForPreset(preset: ManagedDirectoryPreset): CopySpec {
  if (preset === 'platform') {
    return {
      loadFailed: 'Failed to load platform users.',
      lede: 'These accounts use the Root login type and access the admin area (dashboard, licenses, this list). They are separate from law-firm workspace users and from company (client) users.',
      summaryTotal: 'Platform accounts',
      summaryActive: 'Active',
      loading: 'Loading platform users…',
      emptyTitle: 'No platform users in the list',
      emptyBody:
        'If you normally sign in with a Root account, ensure the API database has users with LoginType Root, or invite a new administrator below.',
      emptyInviteCta: 'Invite platform user',
      emptyNoPermission: '',
      searchPlaceholder: 'Search by name, email, or phone…',
      searchAriaLabel: 'Search platform users',
      inviteBtnTitle: 'Invite a platform administrator',
      inviteBtn: 'Invite platform user',
      addTitle: 'Invite platform user',
      addHeroTitle: 'Root administrator',
      addHeroBody: (
        <>
          Creates a Root login with access to this admin area. A temporary password and reset link are generated when
          you save.
        </>
      ),
      addSubmit: 'Invite platform user',
      addSubmitting: 'Inviting…',
      mutateErrorInvite: 'Failed to invite platform user.',
      editTitle: 'Edit platform user',
      editHeroTitle: 'Root account',
      editHeroBody: (
        <>
          Email cannot be changed here. Use <span className="font-medium text-gray-700">Reset password</span> to issue a
          fresh sign-in link.
        </>
      ),
      editSubmit: 'Save changes',
      editSubmitting: 'Saving…',
      mutateErrorEdit: 'Failed to update user.',
      inviteDlgTitle: 'Platform user invited',
      inviteDlgLede:
        'Share the temporary password or reset link with them. Copy these details now—the temporary password is not shown again.',
      kebabEditDesc: 'Name, phone, status',
      noSearchMatches: 'No users match your search.',
      inviteTempPasswordNote:
        'Copy the temporary password and reset link from the next dialog and share them securely—they are not shown again.',
      addIconTitle: 'New platform administrator',
      editIconTitle: 'Platform user',
      emptyIcon: 'shield',
    };
  }
  if (preset === 'firmWorkspace') {
    return {
      loadFailed: 'Failed to load users.',
      lede: 'Firm staff (Admin/Staff) who manage companies and this workspace. This is not where you provision company users who sign in as clients to run Section 280G analyses.',
      summaryTotal: 'Firm accounts',
      summaryActive: 'Active',
      loading: 'Loading users…',
      emptyTitle: 'No firm team accounts yet',
      emptyBody: 'Invite law firm staff who will use this portal (firm login), not company-side 280G users.',
      emptyInviteCta: 'Invite firm member',
      emptyNoPermission: 'No firm accounts yet. Ask a firm admin to add law firm staff.',
      searchPlaceholder: 'Search by name, email, role, or phone…',
      searchAriaLabel: 'Search firm team',
      inviteBtnTitle: 'Add a law firm staff member (firm login)',
      inviteBtn: 'Invite firm member',
      addTitle: 'Invite firm member',
      addHeroTitle: 'Law firm staff account',
      addHeroBody: (
        <>
          Creates a <span className="font-medium text-gray-700">firm</span> login (same portal as you) for Admin or
          Staff. Company users who run 280G analyses in the client app use a separate{' '}
          <span className="font-medium text-gray-700">client</span> account—not created here.
        </>
      ),
      addSubmit: 'Invite firm member',
      addSubmitting: 'Inviting…',
      mutateErrorInvite: 'Failed to invite firm member.',
      editTitle: 'Edit firm member',
      editHeroTitle: 'Firm team account',
      editHeroBody: (
        <>
          Email cannot be changed here. Use <span className="font-medium text-gray-700">Reset password</span> in the
          table to issue a fresh sign-in link for this firm member.
        </>
      ),
      editSubmit: 'Save changes',
      editSubmitting: 'Saving…',
      mutateErrorEdit: 'Failed to update firm member.',
      inviteDlgTitle: 'Firm member invited',
      inviteDlgLede:
        'Share the temporary password or reset link with them. Copy these details now—the temporary password is not shown again.',
      kebabEditDesc: 'Name, phone, role, status',
      noSearchMatches: 'No firm team members match your search.',
      inviteTempPasswordNote:
        'A temporary password and reset link are generated when you invite them. Copy those details from the next dialog and share them securely—they are not shown again.',
      addIconTitle: 'New firm staff account',
      editIconTitle: 'Firm team member',
      emptyIcon: 'userCog',
    };
  }
  return {
    loadFailed: 'Failed to load company users.',
    lede: '',
    summaryTotal: 'Client accounts',
    summaryActive: 'Active',
    loading: 'Loading company users…',
    emptyTitle: 'No client users linked to this company yet.',
    emptyBody: '',
    emptyInviteCta: 'Invite user',
    emptyNoPermission: 'Ask a firm admin to invite client users.',
    searchPlaceholder: 'Search by name, email, role, or phone…',
    searchAriaLabel: 'Search client users',
    inviteBtnTitle: 'Invite a client user for this company',
    inviteBtn: 'Invite user',
    addTitle: 'Invite company user',
    addHeroTitle: 'Client login (280G)',
    addHeroBody: (
      <>
        Creates a <span className="font-medium text-gray-700">client</span> account for this company and your firm
        tenant. They sign in with client login, not firm staff login.
      </>
    ),
    addSubmit: 'Invite user',
    addSubmitting: 'Inviting…',
    mutateErrorInvite: 'Failed to invite company user.',
    editTitle: 'Edit company user',
    editHeroTitle: 'Client account',
    editHeroBody: (
      <>
        Email cannot be changed here. Use <span className="font-medium text-gray-700">Reset password</span> in the table
        to issue a fresh sign-in link.
      </>
    ),
    editSubmit: 'Save changes',
    editSubmitting: 'Saving…',
    mutateErrorEdit: 'Failed to update company user.',
    inviteDlgTitle: 'Company user invited',
    inviteDlgLede:
      'Share the temporary password or reset link with them. Copy these details now—the temporary password is not shown again.',
    kebabEditDesc: 'Name, phone, sign-in',
    noSearchMatches: 'No users match your search.',
    inviteTempPasswordNote:
      'A temporary password and reset link are generated when you invite them. Copy those details from the next dialog—they are not shown again.',
    addIconTitle: 'New client login',
    editIconTitle: 'Client user settings',
    emptyIcon: 'userCog',
  };
}

export type ManagedUserDirectoryPanelProps = {
  preset: ManagedDirectoryPreset;
  adapter: ManagedDirectoryUsersAdapter;
  canManage: boolean;
  /** When false, skips loading (e.g. company tab not visible). */
  enabled?: boolean;
  /** Increment to close add/edit slide-overs (e.g. parent opens another overlay). */
  panelsCloseKey?: number;
  /** Optional header row (e.g. AppPageHeader). */
  pageHeader?: React.ReactNode;
  /** Explainer block above the table (company client tab). */
  intro?: React.ReactNode;
  /** Tighter table cells (embedded company tab). */
  compactTable?: boolean;
  /** Wrap in main + padding (full pages). Default true. */
  includeMainChrome?: boolean;
  idPrefix: string;
};

export default function ManagedUserDirectoryPanel({
  preset,
  adapter,
  canManage,
  enabled = true,
  panelsCloseKey = 0,
  pageHeader = null,
  intro = null,
  compactTable = false,
  includeMainChrome = true,
  idPrefix,
}: ManagedUserDirectoryPanelProps) {
  const copy = useMemo(() => copyForPreset(preset), [preset]);
  const showFirmRole = preset === 'firmWorkspace';
  const showRoleColumn = preset !== 'platform';

  const [users, setUsers] = useState(null as LawFirmUserRow[] | null);
  const [error, setError] = useState(null as string | null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addFirstName, setAddFirstName] = useState('');
  const [addLastName, setAddLastName] = useState('');
  const [addPhone, setAddPhone] = useState('');
  const [addFirmRole, setAddFirmRole] = useState('Staff' as 'Admin' | 'Staff');
  const [addError, setAddError] = useState(null as string | null);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState(null as number | null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editFirmRole, setEditFirmRole] = useState('Staff' as 'Admin' | 'Staff');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editError, setEditError] = useState(null as string | null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [resetPendingUserId, setResetPendingUserId] = useState(null as number | null);
  const [resetLinkError, setResetLinkError] = useState(null as string | null);
  const [resetLinkDialog, setResetLinkDialog] = useState(null as { email: string; passwordResetLink: string } | null);
  const [inviteDialog, setInviteDialog] = useState(
    null as { email: string; temporaryPassword: string; passwordResetLink: string } | null
  );
  const [kebabForUserId, setKebabForUserId] = useState(null as number | null);
  const pageSize = 10;

  function resetAddFormPartial() {
    setAddEmail('');
    setAddFirstName('');
    setAddLastName('');
    setAddPhone('');
    setAddFirmRole('Staff');
    setAddError(null);
  }

  function resetEditFormPartial() {
    setEditingId(null);
    setEditFirstName('');
    setEditLastName('');
    setEditPhone('');
    setEditFirmRole('Staff');
    setEditIsActive(true);
    setEditError(null);
  }

  const panelsClosePrev = useRef<number | null>(null);
  useEffect(() => {
    if (panelsClosePrev.current === null) {
      panelsClosePrev.current = panelsCloseKey;
      return;
    }
    if (panelsCloseKey === panelsClosePrev.current) return;
    panelsClosePrev.current = panelsCloseKey;
    setAddOpen(false);
    setEditOpen(false);
    setEditingId(null);
    resetAddFormPartial();
    resetEditFormPartial();
  }, [panelsCloseKey]);

  function sortRows(rows: LawFirmUserRow[]): LawFirmUserRow[] {
    return [...rows].sort((a, b) => {
      const ln = a.lastName.localeCompare(b.lastName, undefined, { sensitivity: 'base' });
      if (ln !== 0) return ln;
      return a.firstName.localeCompare(b.firstName, undefined, { sensitivity: 'base' });
    });
  }

  function mergeCreatedUser(row: LawFirmUserRow) {
    setUsers((prev) => {
      if (prev === null) return [row];
      const withoutDup = prev.filter((u) => u.id !== row.id);
      return sortRows([...withoutDup, row]);
    });
  }

  function mergeUpdatedUser(row: LawFirmUserRow) {
    setUsers((prev) => {
      if (prev === null) return [row];
      const next = prev.map((u) => (u.id === row.id ? row : u));
      return sortRows(next);
    });
  }

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    setError(null);
    setUsers(null);
    adapter
      .list()
      .then((rows) => {
        if (!cancelled) setUsers(rows);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : copy.loadFailed);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, adapter, copy.loadFailed]);

  const searchTrim = search.trim();
  const searchLower = searchTrim.toLowerCase();
  const filtered =
    users === null
      ? []
      : searchTrim
        ? users.filter((u) => {
            const name = [u.firstName, u.lastName]
              .map((s) => s.trim())
              .filter(Boolean)
              .join(' ')
              .toLowerCase();
            return (
              name.includes(searchLower) ||
              u.email.toLowerCase().includes(searchLower) ||
              (showRoleColumn && u.role.toLowerCase().includes(searchLower)) ||
              (u.phone ?? '').toLowerCase().includes(searchLower)
            );
          })
        : users;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, users?.length]);

  const closeAdd = () => {
    setAddOpen(false);
    resetAddFormPartial();
  };

  const openAdd = () => {
    setEditOpen(false);
    setEditingId(null);
    resetAddFormPartial();
    setAddOpen(true);
  };

  const submitAdd = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const email = addEmail.trim();
    const firstName = addFirstName.trim();
    const lastName = addLastName.trim();
    if (!email) {
      setAddError('Email is required.');
      return;
    }
    if (!firstName || !lastName) {
      setAddError('First and last name are required.');
      return;
    }
    setAddError(null);
    setAddSubmitting(true);
    adapter
      .create({
        email,
        firstName,
        lastName,
        phone: addPhone.trim() || undefined,
        ...(showFirmRole ? { firmRole: addFirmRole } : {}),
      })
      .then(({ user: row, invite }) => {
        mergeCreatedUser(row);
        closeAdd();
        setInviteDialog({
          email: row.email,
          temporaryPassword: invite.temporaryPassword,
          passwordResetLink: invite.passwordResetLink,
        });
      })
      .catch((err: unknown) => {
        setAddError(err instanceof Error ? err.message : copy.mutateErrorInvite);
      })
      .finally(() => setAddSubmitting(false));
  };

  const closeEdit = () => {
    setEditOpen(false);
    resetEditFormPartial();
  };

  const openEdit = (u: LawFirmUserRow) => {
    setAddOpen(false);
    setEditingId(u.id);
    setEditFirstName(u.firstName);
    setEditLastName(u.lastName);
    setEditPhone(u.phone ?? '');
    setEditFirmRole(u.role === 'Admin' ? 'Admin' : 'Staff');
    setEditIsActive(u.isActive);
    setEditError(null);
    setEditOpen(true);
  };

  const submitEdit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (editingId === null) return;
    const firstName = editFirstName.trim();
    const lastName = editLastName.trim();
    if (!firstName || !lastName) {
      setEditError('First and last name are required.');
      return;
    }
    setEditError(null);
    setEditSubmitting(true);
    adapter
      .update(editingId, {
        firstName,
        lastName,
        phone: editPhone.trim() || undefined,
        isActive: editIsActive,
        ...(showFirmRole ? { firmRole: editFirmRole } : {}),
      })
      .then((row) => {
        mergeUpdatedUser(row);
        closeEdit();
      })
      .catch((err: unknown) => {
        setEditError(err instanceof Error ? err.message : copy.mutateErrorEdit);
      })
      .finally(() => setEditSubmitting(false));
  };

  const issueResetLink = async (u: LawFirmUserRow) => {
    setAddOpen(false);
    setEditOpen(false);
    setEditingId(null);
    setResetLinkError(null);
    setResetPendingUserId(u.id);
    try {
      const { passwordResetLink } = await adapter.resetPassword(u.id);
      setResetLinkDialog({ email: u.email, passwordResetLink });
    } catch (err: unknown) {
      setResetLinkError(err instanceof Error ? err.message : 'Failed to issue reset link.');
    } finally {
      setResetPendingUserId(null);
    }
  };

  const totalUsers = users?.length ?? 0;
  const activeUsers = users?.filter((u) => u.isActive).length ?? 0;
  const summaryBadges = [
    { label: copy.summaryTotal, value: String(totalUsers) },
    { label: copy.summaryActive, value: String(activeUsers) },
  ];

  const cellPx = compactTable ? 'px-4 py-2.5' : 'px-5 py-3';
  const headPx = compactTable ? 'px-4 py-2.5' : 'px-5 py-3';

  const renderRow = (u: LawFirmUserRow) => {
    const name = [u.firstName, u.lastName]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(' ');
    const created = u.createdAt.trim() !== '' ? new Date(u.createdAt) : null;
    const createdLabel =
      created && !Number.isNaN(created.getTime())
        ? created.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
        : '—';
    const canEditRow = canManage;
    return (
      <tr key={u.id}>
        <td className={`${cellPx} text-sm text-gray-900`}>{name || '—'}</td>
        <td className={`${cellPx} text-sm text-gray-700`}>{u.email}</td>
        {showRoleColumn ? <td className={`${cellPx} text-sm text-gray-700`}>{u.role}</td> : null}
        <td className={`${cellPx} text-sm text-gray-700`}>{u.isActive ? 'Yes' : 'No'}</td>
        <td className={`${cellPx} text-sm text-gray-700`}>{u.phone ?? '—'}</td>
        <td className={`${cellPx} text-sm text-gray-600 tabular-nums`}>{createdLabel}</td>
        <td className={`${cellPx} text-sm text-right whitespace-nowrap`}>
          {canEditRow ? (
            <div className="flex items-center justify-end gap-1">
              <button
                type="button"
                onClick={() => openEdit(u)}
                className="hidden sm:inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200/90 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/45 focus-visible:ring-offset-1"
                title={`Edit ${copy.kebabEditDesc}`}
                aria-label="Edit user"
              >
                <Pencil size={18} strokeWidth={2} aria-hidden />
              </button>
              <TableRowKebabMenu
                ariaLabel={`Actions for ${u.email}`}
                isOpen={kebabForUserId === u.id}
                onOpenChange={(open) => setKebabForUserId(open ? u.id : null)}
                entries={[
                  {
                    type: 'item',
                    id: 'edit',
                    label: 'Edit',
                    description: copy.kebabEditDesc,
                    icon: <Pencil size={16} strokeWidth={2} aria-hidden />,
                    onClick: () => openEdit(u),
                    tone: 'default',
                  },
                  {
                    type: 'item',
                    id: 'reset',
                    label: resetPendingUserId === u.id ? 'Sending reset link…' : 'Reset password',
                    icon: <KeyRound size={16} strokeWidth={2} aria-hidden />,
                    onClick: () => void issueResetLink(u),
                    tone: 'default',
                  },
                ]}
              />
            </div>
          ) : (
            <span className="text-gray-300">—</span>
          )}
        </td>
      </tr>
    );
  };

  const emptyIcon =
    copy.emptyIcon === 'shield' ? (
      <Shield size={24} className="text-gray-400" />
    ) : (
      <UserCog size={24} className="text-gray-400" />
    );

  const inner = (
    <>
      {pageHeader}
      <div className={`${includeMainChrome ? `flex-1 overflow-auto pb-8 pt-3 sm:pt-4 ${shellMainPaddingX}` : ''}`}>
        <div className="w-full space-y-6 relative">
          {copy.lede ? (
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <p className="text-sm text-gray-500 max-w-2xl">{copy.lede}</p>
              {users !== null && (
                <div className="flex flex-wrap items-center gap-2 justify-end ml-auto">
                  {summaryBadges.map(({ label, value }) => (
                    <span
                      key={label}
                      className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-white/80 backdrop-blur-sm border border-gray-200/60 text-gray-700 shadow-sm"
                    >
                      <span className="text-gray-500 mr-1.5">{label}:</span>
                      <span className="font-medium text-gray-900 tabular-nums">{value}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            users !== null && (
              <div className="flex flex-wrap items-center gap-2 justify-end">
                {summaryBadges.map(({ label, value }) => (
                  <span
                    key={label}
                    className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-white/80 backdrop-blur-sm border border-gray-200/60 text-gray-700 shadow-sm"
                  >
                    <span className="text-gray-500 mr-1.5">{label}:</span>
                    <span className="font-medium text-gray-900 tabular-nums">{value}</span>
                  </span>
                ))}
              </div>
            )
          )}

          {intro}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
          )}

          {resetLinkError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {resetLinkError}
            </div>
          )}

          {!enabled ? null : users === null && !error ? (
            <div className={`${clientSurfaceCardClass} p-8 text-center text-sm text-gray-500`}>{copy.loading}</div>
          ) : null}

          {enabled && users !== null && users.length === 0 && !error ? (
            <div className={clientEmptyStateCardClass}>
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                {emptyIcon}
              </div>
              <p className="text-gray-600 font-medium">{copy.emptyTitle}</p>
              {copy.emptyBody ? <p className="text-sm text-gray-500 mt-1 mb-6">{copy.emptyBody}</p> : null}
              {canManage ? (
                <button
                  type="button"
                  onClick={openAdd}
                  title={copy.inviteBtnTitle}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg btn-primary text-sm font-medium focus:ring-2 focus:ring-emerald-600/50 focus:ring-offset-2"
                >
                  <UserPlus size={18} aria-hidden />
                  {copy.emptyInviteCta}
                </button>
              ) : copy.emptyNoPermission ? (
                <p className="mt-2 text-sm text-gray-500">{copy.emptyNoPermission}</p>
              ) : null}
            </div>
          ) : null}

          {enabled && users !== null && users.length > 0 && !error ? (
            <div
              className={
                compactTable
                  ? 'overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm'
                  : clientDataPanelClass
              }
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-5 py-4 border-b border-gray-200 bg-gray-50/80">
                <div className="relative w-full sm:flex-1 sm:min-w-0 sm:max-w-md">
                  <Search
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                    aria-hidden
                    title="Search"
                  />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={copy.searchPlaceholder}
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white placeholder:text-gray-400 focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none"
                    aria-label={copy.searchAriaLabel}
                  />
                </div>
                {canManage ? (
                  <button
                    type="button"
                    onClick={openAdd}
                    title={copy.inviteBtnTitle}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg btn-primary text-sm font-medium transition-colors shrink-0 focus:ring-2 focus:ring-emerald-600/50 focus:ring-offset-2"
                  >
                    <UserPlus size={18} aria-hidden />
                    {copy.inviteBtn}
                  </button>
                ) : null}
              </div>
              <table className={`w-full ${compactTable ? 'min-w-[700px] text-sm' : ''}`}>
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className={`${headPx} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                      Name
                    </th>
                    <th className={`${headPx} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                      Email
                    </th>
                    {showRoleColumn ? (
                      <th className={`${headPx} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                        Role
                      </th>
                    ) : null}
                    <th className={`${headPx} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                      Active
                    </th>
                    <th className={`${headPx} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                      Phone
                    </th>
                    <th className={`${headPx} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                      Created
                    </th>
                    <th
                      className={`${headPx} text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[1%] whitespace-nowrap`}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={compactTable ? 'divide-y divide-gray-100' : 'divide-y divide-gray-200'}>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={showRoleColumn ? 7 : 6} className={`${cellPx} text-center text-sm text-gray-500`}>
                        {copy.noSearchMatches}
                      </td>
                    </tr>
                  ) : (
                    paginated.map((u) => renderRow(u))
                  )}
                </tbody>
              </table>
              {filtered.length > pageSize ? (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-600">
                  <div>
                    Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * pageSize, filtered.length)}</span> of{' '}
                    <span className="font-medium">{filtered.length}</span>
                    {searchTrim ? (
                      <>
                        {' '}
                        (<span className="font-medium">{users.length}</span> total)
                      </>
                    ) : null}
                  </div>
                  <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 border border-gray-300 rounded-l-md text-xs font-medium bg-white hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1.5 text-xs border-y border-gray-300 bg-white">
                      Page <span className="font-medium">{currentPage}</span> of{' '}
                      <span className="font-medium">{totalPages}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 border border-gray-300 rounded-r-md text-xs font-medium bg-white hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white"
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <div
        className={`fixed inset-0 z-50 transition-opacity duration-200 ${addOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="absolute inset-0 bg-black/40 transition-opacity duration-200" onClick={closeAdd} aria-hidden />
        <div
          className={`absolute top-0 right-0 h-full w-full max-w-lg bg-white shadow-xl flex flex-col transition-transform duration-300 ease-out ${
            addOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col flex-1 min-h-0">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h3 className="font-medium text-gray-900">{copy.addTitle}</h3>
              <button
                type="button"
                onClick={closeAdd}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                aria-label="Close"
                title="Close"
              >
                <XCircle size={20} aria-hidden />
              </button>
            </div>
            <form onSubmit={submitAdd} className="flex flex-col flex-1 min-h-0">
              <div className="p-6 space-y-5 overflow-y-auto flex-1">
                <div className="flex items-start gap-3">
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"
                    title={copy.addIconTitle}
                  >
                    <UserPlus size={20} aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{copy.addHeroTitle}</p>
                    <p className="text-sm text-gray-500 mt-0.5">{copy.addHeroBody}</p>
                  </div>
                </div>

                {addError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    {addError}
                  </div>
                )}

                <div className="space-y-4">
                  {showFirmRole ? (
                    <fieldset>
                      <legend className="block text-xs font-medium text-gray-600 mb-2">Role</legend>
                      <div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
                        <label className="inline-flex items-center gap-2.5 text-sm text-gray-800 cursor-pointer">
                          <input
                            type="radio"
                            name={`${idPrefix}-firmRole`}
                            value="Admin"
                            checked={addFirmRole === 'Admin'}
                            onChange={() => setAddFirmRole('Admin')}
                            className="h-4 w-4 border-gray-300 text-emerald-600 focus:ring-emerald-600"
                          />
                          Admin
                        </label>
                        <label className="inline-flex items-center gap-2.5 text-sm text-gray-800 cursor-pointer">
                          <input
                            type="radio"
                            name={`${idPrefix}-firmRole`}
                            value="Staff"
                            checked={addFirmRole === 'Staff'}
                            onChange={() => setAddFirmRole('Staff')}
                            className="h-4 w-4 border-gray-300 text-emerald-600 focus:ring-emerald-600"
                          />
                          Staff
                        </label>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Admins can invite firm members; Staff use the same workspace with limited access.
                      </p>
                    </fieldset>
                  ) : null}
                  <div>
                    <label htmlFor={`${idPrefix}-add-email`} className="block text-xs font-medium text-gray-600 mb-1">
                      Email
                    </label>
                    <input
                      id={`${idPrefix}-add-email`}
                      type="email"
                      autoComplete="email"
                      value={addEmail}
                      onChange={(e) => setAddEmail(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2">
                    {copy.inviteTempPasswordNote}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor={`${idPrefix}-add-first`} className="block text-xs font-medium text-gray-600 mb-1">
                        First name
                      </label>
                      <input
                        id={`${idPrefix}-add-first`}
                        type="text"
                        autoComplete="given-name"
                        value={addFirstName}
                        onChange={(e) => setAddFirstName(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor={`${idPrefix}-add-last`} className="block text-xs font-medium text-gray-600 mb-1">
                        Last name
                      </label>
                      <input
                        id={`${idPrefix}-add-last`}
                        type="text"
                        autoComplete="family-name"
                        value={addLastName}
                        onChange={(e) => setAddLastName(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor={`${idPrefix}-add-phone`} className="block text-xs font-medium text-gray-600 mb-1">
                      Phone <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      id={`${idPrefix}-add-phone`}
                      type="tel"
                      autoComplete="tel"
                      value={addPhone}
                      onChange={(e) => setAddPhone(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none"
                    />
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={closeAdd}
                  className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                  disabled={addSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg btn-primary text-sm font-medium disabled:opacity-60"
                  disabled={addSubmitting}
                >
                  {addSubmitting ? copy.addSubmitting : copy.addSubmit}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div
        className={`fixed inset-0 z-50 transition-opacity duration-200 ${editOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="absolute inset-0 bg-black/40 transition-opacity duration-200" onClick={closeEdit} aria-hidden />
        <div
          className={`absolute top-0 right-0 h-full w-full max-w-lg bg-white shadow-xl flex flex-col transition-transform duration-300 ease-out ${
            editOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col flex-1 min-h-0">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h3 className="font-medium text-gray-900">{copy.editTitle}</h3>
              <button
                type="button"
                onClick={closeEdit}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                aria-label="Close"
                title="Close"
              >
                <XCircle size={20} aria-hidden />
              </button>
            </div>
            <form onSubmit={submitEdit} className="flex flex-col flex-1 min-h-0">
              <div className="p-6 space-y-5 overflow-y-auto flex-1">
                <div className="flex items-start gap-3">
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"
                    title={copy.editIconTitle}
                  >
                    <UserCog size={20} aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{copy.editHeroTitle}</p>
                    <p className="text-sm text-gray-500 mt-0.5">{copy.editHeroBody}</p>
                  </div>
                </div>

                {editError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    {editError}
                  </div>
                )}

                <div className="space-y-4">
                  {showFirmRole ? (
                    <fieldset>
                      <legend className="block text-xs font-medium text-gray-600 mb-2">Role</legend>
                      <div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
                        <label className="inline-flex items-center gap-2.5 text-sm text-gray-800 cursor-pointer">
                          <input
                            type="radio"
                            name={`${idPrefix}-editFirmRole`}
                            value="Admin"
                            checked={editFirmRole === 'Admin'}
                            onChange={() => setEditFirmRole('Admin')}
                            className="h-4 w-4 border-gray-300 text-emerald-600 focus:ring-emerald-600"
                          />
                          Admin
                        </label>
                        <label className="inline-flex items-center gap-2.5 text-sm text-gray-800 cursor-pointer">
                          <input
                            type="radio"
                            name={`${idPrefix}-editFirmRole`}
                            value="Staff"
                            checked={editFirmRole === 'Staff'}
                            onChange={() => setEditFirmRole('Staff')}
                            className="h-4 w-4 border-gray-300 text-emerald-600 focus:ring-emerald-600"
                          />
                          Staff
                        </label>
                      </div>
                    </fieldset>
                  ) : null}
                  <div>
                    <p className="block text-xs font-medium text-gray-600 mb-1">Email</p>
                    <p className="text-sm text-gray-900 py-2 px-3 rounded-lg bg-gray-50 border border-gray-100 break-all">
                      {users !== null && editingId !== null
                        ? (users.find((x) => x.id === editingId)?.email ?? '—')
                        : '—'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Sign-in email cannot be changed.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor={`${idPrefix}-edit-first`}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        First name
                      </label>
                      <input
                        id={`${idPrefix}-edit-first`}
                        type="text"
                        autoComplete="given-name"
                        value={editFirstName}
                        onChange={(e) => setEditFirstName(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor={`${idPrefix}-edit-last`} className="block text-xs font-medium text-gray-600 mb-1">
                        Last name
                      </label>
                      <input
                        id={`${idPrefix}-edit-last`}
                        type="text"
                        autoComplete="family-name"
                        value={editLastName}
                        onChange={(e) => setEditLastName(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor={`${idPrefix}-edit-phone`} className="block text-xs font-medium text-gray-600 mb-1">
                      Phone <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      id={`${idPrefix}-edit-phone`}
                      type="tel"
                      autoComplete="tel"
                      value={editPhone}
                      onChange={(e) => setEditPhone(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none"
                    />
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editIsActive}
                      onChange={(e) => setEditIsActive(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-600"
                    />
                    Active (can sign in)
                  </label>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={closeEdit}
                  className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                  disabled={editSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg btn-primary text-sm font-medium disabled:opacity-60"
                  disabled={editSubmitting}
                >
                  {editSubmitting ? copy.editSubmitting : copy.editSubmit}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <PasswordResetLinkDialog
        open={resetLinkDialog !== null}
        email={resetLinkDialog?.email ?? ''}
        passwordResetLink={resetLinkDialog?.passwordResetLink ?? ''}
        onClose={() => setResetLinkDialog(null)}
      />

      <UserInviteCredentialsDialog
        open={inviteDialog !== null}
        title={copy.inviteDlgTitle}
        lede={copy.inviteDlgLede}
        data={inviteDialog}
        onClose={() => setInviteDialog(null)}
      />
    </>
  );

  if (includeMainChrome) {
    return <main className="flex-1 flex flex-col min-h-0 min-w-0">{inner}</main>;
  }

  return <>{inner}</>;
}
